import { useParams, Link, useNavigate } from 'react-router-dom';
import { useMemo } from 'react';
import { getBlogArticles } from '../../../mocks/blog';
import { useScrollReveal } from '../../../hooks/useScrollReveal';
import { useSEO } from '../../../hooks/useSEO';
import { SchemaMarkup } from '../../../components/SEOHead';

export default function BlogArticleDetail() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { ref, isVisible, style } = useScrollReveal({ threshold: 0.05, distance: 36, duration: 900 });

  const articles = getBlogArticles();
  const article = useMemo(() => articles.find((a) => a.slug === slug), [articles, slug]);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.id !== article.id && a.category === article.category)
      .slice(0, 3);
  }, [article, articles]);

  useSEO({
    title: article ? `${article.title} | Blog Moda | Anthony Sánchez Couture` : 'Artículo | Blog de Moda',
    description: article ? article.excerpt.substring(0, 160) : 'Artículo de moda y alta costura del blog de Anthony Sánchez Couture en Barcelona.',
    canonical: article ? `/blog/${article.slug}` : '/blog',
    ogTitle: article ? article.title : 'Artículo',
    ogDescription: article ? article.excerpt.substring(0, 200) : 'Artículo sobre moda y alta costura desde Barcelona.',
    ogType: 'article',
  });

  const articleSchema = article ? {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    author: {
      '@type': 'Person',
      name: article.author,
      jobTitle: article.authorRole,
    },
    publisher: {
      '@type': 'LocalBusiness',
      name: 'Anthony Sánchez Couture',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Rambla de Catalunya, 85, Principal - 2',
        addressLocality: 'Barcelona',
        addressRegion: 'Cataluña',
        postalCode: '08008',
        addressCountry: 'ES',
      },
    },
    datePublished: article.date,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://anthonysanchezc.com/blog/${article.slug}`,
    },
  } : null;

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Inicio',
        item: 'https://anthonysanchezc.com',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Blog',
        item: 'https://anthonysanchezc.com/blog',
      },
      article ? {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
        item: `https://anthonysanchezc.com/blog/${article.slug}`,
      } : null,
    ].filter(Boolean),
  };

  if (!article) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-serif text-brand-black text-2xl font-semibold">
            Artículo no encontrado
          </h2>
          <p className="font-sans text-brand-gray-500 mt-4">
            El artículo que buscas no existe o ha sido movido.
          </p>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 font-sans text-brand-black text-[13px] tracking-widest uppercase mt-6 hover:text-brand-gray-500 transition-colors"
          >
            <i className="ri-arrow-left-line" />
            Volver al blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <SchemaMarkup schema={articleSchema || breadcrumbSchema} />
      <SchemaMarkup schema={breadcrumbSchema} />
      {/* Article Header */}
      <section className="relative w-full bg-brand-black overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-brand-black/70 via-brand-black/50 to-brand-black/90" />
        </div>

        <div className="relative z-10 px-6 lg:px-[7%] pt-32 pb-16 md:pt-40 md:pb-24">
          <button
            onClick={() => navigate('/blog')}
            className="inline-flex items-center gap-2 font-sans text-white/70 text-sm hover:text-white transition-colors mb-8"
          >
            <i className="ri-arrow-left-line" />
            Volver al blog
          </button>

          <div className="max-w-3xl" style={style}>
            <div className="flex items-center gap-3 mb-6">
              <span className="bg-white/20 backdrop-blur-sm text-white font-sans text-[11px] tracking-[0.15em] uppercase px-4 py-2 rounded-full">
                {article.category}
              </span>
              <span className="text-white/50 text-sm">{article.readTime} de lectura</span>
            </div>

            <h1 className="font-serif text-white text-[clamp(2rem,4vw,3.5rem)] font-semibold leading-[1.15]">
              {article.title}
            </h1>

            <div className="mt-8 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                <span className="font-serif text-white text-lg font-medium">A</span>
              </div>
              <div>
                <p className="font-sans text-white font-medium">{article.author}</p>
                <p className="font-sans text-white/60 text-sm">{article.authorRole}</p>
              </div>
              <span className="text-white/30 mx-2">|</span>
              <span className="font-sans text-white/60 text-sm">{article.date}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section ref={ref} className="w-full bg-white py-16 md:py-24">
        <div className="px-6 lg:px-[7%]">
          <div className="max-w-3xl mx-auto">
            {/* Featured image */}
            <div
              className="rounded-3xl overflow-hidden mb-12"
              style={isVisible ? {
                opacity: 1,
                transform: 'translateY(0px)',
                transition: 'opacity 900ms cubic-bezier(0.25, 0.46, 0.45, 0.94) 100ms, transform 900ms cubic-bezier(0.25, 0.46, 0.45, 0.94) 100ms',
              } : {
                opacity: 0,
                transform: 'translateY(30px)',
                transition: 'opacity 900ms cubic-bezier(0.25, 0.46, 0.45, 0.94) 100ms, transform 900ms cubic-bezier(0.25, 0.46, 0.45, 0.94) 100ms',
                willChange: 'opacity, transform',
              }}
            >
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-[300px] md:h-[500px] object-cover"
              />
            </div>

            {/* Tags */}
            <div
              className="flex flex-wrap gap-2 mb-10"
              style={isVisible ? {
                opacity: 1,
                transform: 'translateY(0px)',
                transition: 'opacity 800ms cubic-bezier(0.25, 0.46, 0.45, 0.94) 180ms, transform 800ms cubic-bezier(0.25, 0.46, 0.45, 0.94) 180ms',
              } : {
                opacity: 0,
                transform: 'translateY(20px)',
                transition: 'opacity 800ms cubic-bezier(0.25, 0.46, 0.45, 0.94) 180ms, transform 800ms cubic-bezier(0.25, 0.46, 0.45, 0.94) 180ms',
                willChange: 'opacity, transform',
              }}
            >
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="font-sans text-brand-gray-500 text-xs px-4 py-2 bg-brand-gray-50 rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>

            {/* Article body */}
            <div className="space-y-8">
              {article.content.map((paragraph, index) => (
                <p
                  key={index}
                  className="font-sans text-brand-gray-600 text-[16px] md:text-[17px] leading-[1.85]"
                  style={isVisible ? {
                    opacity: 1,
                    transform: 'translateY(0px)',
                    transition: `opacity 800ms cubic-bezier(0.25, 0.46, 0.45, 0.94) ${250 + index * 80}ms, transform 800ms cubic-bezier(0.25, 0.46, 0.45, 0.94) ${250 + index * 80}ms`,
                  } : {
                    opacity: 0,
                    transform: 'translateY(20px)',
                    transition: `opacity 800ms cubic-bezier(0.25, 0.46, 0.45, 0.94) ${250 + index * 80}ms, transform 800ms cubic-bezier(0.25, 0.46, 0.45, 0.94) ${250 + index * 80}ms`,
                    willChange: 'opacity, transform',
                  }}
                >
                  {paragraph}
                </p>
              ))}
            </div>

            {/* Author signature */}
            <div
              className="mt-16 pt-10 border-t border-brand-gray-200"
              style={isVisible ? {
                opacity: 1,
                transform: 'translateY(0px)',
                transition: `opacity 800ms cubic-bezier(0.25, 0.46, 0.45, 0.94) ${250 + article.content.length * 80}ms, transform 800ms cubic-bezier(0.25, 0.46, 0.45, 0.94) ${250 + article.content.length * 80}ms`,
              } : {
                opacity: 0,
                transform: 'translateY(20px)',
                transition: `opacity 800ms cubic-bezier(0.25, 0.46, 0.45, 0.94) ${250 + article.content.length * 80}ms, transform 800ms cubic-bezier(0.25, 0.46, 0.45, 0.94) ${250 + article.content.length * 80}ms`,
                willChange: 'opacity, transform',
              }}
            >
              <div className="flex items-start gap-5">
                <div className="w-14 h-14 rounded-full bg-brand-black flex items-center justify-center flex-shrink-0">
                  <span className="font-serif text-white text-xl font-medium">A</span>
                </div>
                <div>
                  <p className="font-serif text-brand-black text-lg font-semibold">
                    {article.author}
                  </p>
                  <p className="font-sans text-brand-gray-500 text-sm mt-1">
                    {article.authorRole} en Anthony Sánchez Couture. Atelier de alta costura
                    artesanal en Barcelona desde 2017.
                  </p>
                </div>
              </div>
            </div>

            {/* Share */}
            <div
              className="mt-10 flex items-center gap-4"
              style={isVisible ? {
                opacity: 1,
                transform: 'translateY(0px)',
                transition: `opacity 800ms cubic-bezier(0.25, 0.46, 0.45, 0.94) ${300 + article.content.length * 80}ms, transform 800ms cubic-bezier(0.25, 0.46, 0.45, 0.94) ${300 + article.content.length * 80}ms`,
              } : {
                opacity: 0,
                transform: 'translateY(20px)',
                transition: `opacity 800ms cubic-bezier(0.25, 0.46, 0.45, 0.94) ${300 + article.content.length * 80}ms, transform 800ms cubic-bezier(0.25, 0.46, 0.45, 0.94) ${300 + article.content.length * 80}ms`,
                willChange: 'opacity, transform',
              }}
            >
              <span className="font-sans text-brand-gray-400 text-sm">Compartir:</span>
              <button
                onClick={() =>
                  navigator.clipboard.writeText(window.location.href).catch(() => {})
                }
                className="w-10 h-10 rounded-full bg-brand-gray-50 flex items-center justify-center text-brand-gray-500 hover:bg-brand-black hover:text-white transition-colors"
                aria-label="Copiar enlace"
              >
                <i className="ri-link" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="w-full bg-brand-cream py-16 md:py-24">
          <div className="px-6 lg:px-[7%]">
            <h2 className="font-serif text-brand-black text-2xl md:text-3xl font-semibold mb-10">
              Artículos <span className="italic text-brand-gray-300">relacionados</span>
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
              {relatedArticles.map((related) => (
                <article key={related.id} className="bg-white rounded-3xl overflow-hidden group">
                  <Link to={`/blog/${related.slug}`} className="block">
                    <div className="relative h-[200px] md:h-[240px] overflow-hidden">
                      <img
                        src={related.image}
                        alt={related.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                      <div className="absolute top-4 left-4">
                        <span className="bg-white/90 backdrop-blur-sm text-brand-black font-sans text-[11px] tracking-[0.15em] uppercase px-3 py-1.5 rounded-full">
                          {related.category}
                        </span>
                      </div>
                    </div>
                  </Link>
                  <div className="p-6">
                    <div className="flex items-center gap-3 text-brand-gray-400 text-sm">
                      <span>{related.date}</span>
                      <span className="w-1 h-1 rounded-full bg-brand-gray-300" />
                      <span>{related.readTime}</span>
                    </div>
                    <Link to={`/blog/${related.slug}`}>
                      <h3 className="font-serif text-brand-black text-lg font-semibold leading-[1.35] mt-3 group-hover:text-brand-gray-500 transition-colors">
                        {related.title}
                      </h3>
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}